import React, { useCallback, useMemo } from 'react';
import { Icon } from '../../design-system/icons/Icon';
import { Button, FilterPill } from '../../design-system/ui';
import { CompanyName } from '../CompanyName';
import { usePushIndexUiState } from '../../hooks/usePushIndexUiState';
import { useSearchSort, useSearchContext } from '../../contexts/SearchContext';
import { formatFilterLabel, REMOTE_VALUES, HYBRID_VALUES } from '../../utils/facets/format-filter-label';
import { getKnownSingleValueFilterLabel } from '../../utils/facets/single-value-filter-label';
import { resolveLastSeenLabel } from '../LastSeenSelector';
import {
  shouldIncludeFacetInTopFilters,
  TOP_FILTER_ATTRIBUTE_ORDER,
  getTopFilterColorClass,
  type TopFilterMode,
} from '../../layouts/helpers/topFilterConfig';
import { supportsOnlyFilterMode } from '../../utils/typesense/array-facet-count-fields';
import { useScrollCollapse } from '../navigation/nav-live-index/useScrollCollapse';
import { tapeMessages } from './tape-messages';
import { titleValuesMatchRole } from '../../utils/role/role-hub-config';
import { findRoleForNormalisedTitle } from '../../utils/role/find-role-for-title';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';

// The location pills read the `regionTags` facet, the same one LocationFilterSection
// (FilterBuildingBlocks.tsx) writes, so a selection made in the Advanced Filters modal shows up here.
const locationAttribute = 'regionTags';
const locationExcludeKey = `exclude_${locationAttribute}`;

const EXCLUDED_FROM_ADDITIONAL_FACETS = new Set(['firstSeenMin']);
import { Logo } from '../navigation/Logo';
import type { IntlShape } from 'react-intl';

export interface FacetItem {
  attribute: string;
  value: string;
  label: string;
  count: number;
  isActive: boolean;
  isExcluded: boolean;
  /** "Only this value" (exact-match) state, only ever true for array facets (ARRAY_FACET_COUNT_FIELD). */
  isOnly: boolean;
  colorClass: string;
  icon?: React.ReactNode;
  locationGroup?: 'finland' | 'remote';
}

/** Stands in for a role's whole normalisedTitle set when the tape names the role once. */
const ROLE_TITLES_VALUE = '__role_titles__';

function facetMode(isExcluded: boolean, isOnly: boolean): TopFilterMode {
  return isExcluded ? 'exclude' : isOnly ? 'only' : 'include';
}

export interface TopFiltersProps {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  facetsData: Record<string, Array<{ value: string; label: string; count: number }>>;
  filtersObj: Record<string, string[]>;
  query?: string;
  /** When provided, shows the logo on the left on small screens */
  clearSearch?: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
}

/**
 * The SRP filter tape: one token per applied filter attribute ("or"/"and"/"not"/"only" words),
 * quick-add pills for suggested facets, the sort note and "Clear all". Only mounted by
 * DesktopFilterCategories when at least one filter is applied (unfiltered, the SRP shows the
 * index strip instead, see IndexStrip.tsx).
 */
export const TopFilters: React.FC<TopFiltersProps> = ({
  intl,
  formatMessage,
  facetsData,
  filtersObj,
  query,
  clearSearch,
}) => {
  const { pushIndexUiState } = usePushIndexUiState();
  const { sort } = useSearchSort();
  // The tape's quick-adds are checked against the live result count.
  const { results } = useSearchContext();
  const tapeCollapsed = useScrollCollapse(64);


  // Helper function for case-insensitive facet value comparison
  const isFacetValueInArray = useCallback((value: string, array: string[] | undefined): boolean => {
    if (!array || array.length === 0) return false;
    return array.some((v) => v.toLowerCase() === value.toLowerCase());
  }, []);

  // Combine all facets - memoize expensive calculation
  const allFacetsRaw = useMemo<FacetItem[]>(() => {
    try {
      const locationItems = facetsData[locationAttribute] || [];
      const remotenessItems = facetsData.remoteness || [];
      const jobTypeItems = facetsData.jobType || [];
      const roleLevelItems = facetsData.normalisedRoleLevel || [];
      const companyItems = facetsData.companyId || [];
      const companyIndustryItems = facetsData.companyIndustry || [];
      const requiredLanguagesItems = facetsData.requiredLanguages || [];
      const benefitsItems = facetsData.benefits || [];
      const skillsAndTechnologiesItems = facetsData.skillsAndTechnologies || [];
      const fieldItems = facetsData.field || [];
      const educationLevelItems = facetsData.educationLevel || [];
      const peopleManagementItems = facetsData.peopleManagementTier || [];

      return [
        // Remoteness filters
        ...remotenessItems.map((item) => {
          const excludeKey = 'exclude_remoteness';
          const isActive = isFacetValueInArray(item.value, filtersObj.remoteness);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          return {
            attribute: 'remoteness',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('remoteness', facetMode(isExcluded, false)),
            icon: <Icon name="mapPin" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Location filters (simplified - full implementation would include location grouping)
        ...locationItems.map((item) => {
          const isActive = isFacetValueInArray(item.value, filtersObj[locationAttribute]);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[locationExcludeKey]);
          return {
            attribute: locationAttribute,
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass(locationAttribute, facetMode(isExcluded, false)),
            icon: <Icon name="mapPin" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Job Type filters
        ...jobTypeItems.map((item) => {
          const excludeKey = 'exclude_jobType';
          const isActive = isFacetValueInArray(item.value, filtersObj.jobType);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          return {
            attribute: 'jobType',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('jobType', facetMode(isExcluded, false)),
            icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Role Level filters
        ...roleLevelItems.map((item) => {
          const excludeKey = 'exclude_normalisedRoleLevel';
          const isActive = isFacetValueInArray(item.value, filtersObj.normalisedRoleLevel);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_normalisedRoleLevel);
          return {
            attribute: 'normalisedRoleLevel',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('normalisedRoleLevel', facetMode(isExcluded, isOnly)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Company Industry filters
        ...companyIndustryItems.map((item) => {
          const excludeKey = 'exclude_companyIndustry';
          const isActive = isFacetValueInArray(item.value, filtersObj.companyIndustry);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_companyIndustry);
          return {
            attribute: 'companyIndustry',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('companyIndustry', facetMode(isExcluded, isOnly)),
            icon: <Icon name="buildingOffice2" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Company filters
        ...companyItems.map((item) => {
          const excludeKey = 'exclude_companyId';
          const isActive = filtersObj.companyId?.includes(item.value) || false;
          const isExcluded = filtersObj[excludeKey]?.includes(item.value) || false;
          return {
            attribute: 'companyId',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('companyId', facetMode(isExcluded, false)),
            icon: <Icon name="buildingOffice2" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Required Languages (same order as ALL_TOP_FILTER_ATTRIBUTES_ORDER)
        ...requiredLanguagesItems.map((item) => {
          const excludeKey = 'exclude_requiredLanguages';
          const isActive = isFacetValueInArray(item.value, filtersObj.requiredLanguages);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_requiredLanguages);
          return {
            attribute: 'requiredLanguages',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('requiredLanguages', facetMode(isExcluded, isOnly)),
            icon: <Icon name="globeAlt" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Benefits
        ...benefitsItems.map((item) => {
          const excludeKey = 'exclude_benefits';
          const isActive = isFacetValueInArray(item.value, filtersObj.benefits);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_benefits);
          return {
            attribute: 'benefits',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('benefits', facetMode(isExcluded, isOnly)),
            icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Skills and Technologies
        ...skillsAndTechnologiesItems.map((item) => {
          const excludeKey = 'exclude_skillsAndTechnologies';
          const isActive = isFacetValueInArray(item.value, filtersObj.skillsAndTechnologies);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_skillsAndTechnologies);
          return {
            attribute: 'skillsAndTechnologies',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('skillsAndTechnologies', facetMode(isExcluded, isOnly)),
            icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Field
        ...fieldItems.map((item) => {
          const excludeKey = 'exclude_field';
          const isActive = isFacetValueInArray(item.value, filtersObj.field);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_field);
          return {
            attribute: 'field',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('field', facetMode(isExcluded, isOnly)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Education Level
        ...educationLevelItems.map((item) => {
          const excludeKey = 'exclude_educationLevel';
          const isActive = isFacetValueInArray(item.value, filtersObj.educationLevel);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_educationLevel);
          return {
            attribute: 'educationLevel',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('educationLevel', facetMode(isExcluded, isOnly)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
        ...peopleManagementItems.map((item) => {
          const excludeKey = 'exclude_peopleManagementTier';
          const isActive = isFacetValueInArray(item.value, filtersObj.peopleManagementTier);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          return {
            attribute: 'peopleManagementTier',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('peopleManagementTier', facetMode(isExcluded, false)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
      ];
    } catch (error) {
      return [];
    }
  }, [facetsData, filtersObj, isFacetValueInArray]);

  // Get all active facet attributes and calculate additional facets
  const additionalFacets = useMemo(() => {
    try {
      const activeFacetKeys = new Set<string>();
      Object.entries(filtersObj).forEach(([attribute, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          activeFacetKeys.add(attribute);
        }
      });

      // `exclude_<attr>`/`only_<attr>` is a distinct string from `<attr>`; strip the prefix so
      // every key resolves back to the real facet attribute it modifies (company, remoteness, ...).
      const baseAttributeOf = (attr: string): string =>
        attr.startsWith('exclude_')
          ? attr.slice('exclude_'.length)
          : attr.startsWith('only_')
            ? attr.slice('only_'.length)
            : attr;

      // Dedup at the (attribute, value) level, not the attribute level: an attribute can have
      // some of its active values already present in allFacetsRaw (because the search backend's
      // facet-count response happened to include their bucket) while other active values of that
      // *same* attribute are missing from that response (e.g. a bucket with 0 results in
      // combination with the other active filters). Deduping by attribute alone treated the whole
      // attribute as "already shown" the moment any one value appeared, silently dropping chips
      // for the other active values. See the mobile bug report: applying both "Node.js" and
      // "React" under skillsAndTechnologies rendered only the "Node.js" chip.
      const existingActiveValueKeys = new Set(
        allFacetsRaw
          .filter((f) => f.isActive || f.isExcluded || f.isOnly)
          .map((f) => `${f.attribute}::${f.value.toLowerCase()}`),
      );

      const result: FacetItem[] = [];
      activeFacetKeys.forEach((key) => {
        if (EXCLUDED_FROM_ADDITIONAL_FACETS.has(key)) return;

        const attribute = baseAttributeOf(key);
        const items = facetsData[attribute] || [];
        const activeValues = filtersObj[key] || [];
        const excludeKey = `exclude_${attribute}`;

        activeValues.forEach((value) => {
          const dedupeKey = `${attribute}::${value.toLowerCase()}`;
          if (existingActiveValueKeys.has(dedupeKey)) return;
          existingActiveValueKeys.add(dedupeKey);

          const facetItem = items.find((item) => item.value.toLowerCase() === value.toLowerCase());
          const isExcluded = isFacetValueInArray(value, filtersObj[excludeKey]);
          const isOnly =
            supportsOnlyFilterMode(attribute) && isFacetValueInArray(value, filtersObj[`only_${attribute}`]);
          if (facetItem) {
            result.push({
              attribute,
              value: facetItem.value,
              label: facetItem.label,
              count: facetItem.count,
              isActive: true,
              isExcluded,
              isOnly,
              colorClass: getTopFilterColorClass(attribute, facetMode(isExcluded, isOnly)),
              icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
            });
          } else {
            // Pseudo-boolean/single-numeric attributes (isDriversLicenseRequired, salaryRangeMin,
            // etc.) aren't real facets — there's no `facetItem` for them above, and
            // formatFilterLabel's generic capitalize-first-letter fallback would show the raw
            // stored value ("True", "5000") instead of a real label. Use the same message the
            // filter's own toggle/slider shows when this is one of those.
            result.push({
              attribute,
              value,
              label:
                getKnownSingleValueFilterLabel(attribute, value, formatMessage) ?? formatFilterLabel(attribute, value),
              count: 0,
              isActive: true,
              isExcluded,
              isOnly,
              colorClass: getTopFilterColorClass(attribute, facetMode(isExcluded, isOnly)),
              icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
            });
          }
        });
      });
      return result;
    } catch (error) {
      return [];
    }
  }, [allFacetsRaw, filtersObj, facetsData, isFacetValueInArray, formatMessage]);

  // Combine predefined facets with additional active facets
  const allFacetsWithActive = useMemo(() => [...allFacetsRaw, ...additionalFacets], [allFacetsRaw, additionalFacets]);

  // Filter out facets that should never appear in the top filter row (e.g. firstSeenMin only lives in the selector)
  // Job titles stay out of the suggestions, but an APPLIED title must show: hiding it left the
  // strip reading "Helsinki, Finland" alone on an electrician-in-Helsinki search (user feedback
  // 2026-10-08), so nothing outside the filters modal said a job filter was on at all.
  const allowedFacets = useMemo(
    () =>
      allFacetsWithActive.filter(
        (facet) =>
          shouldIncludeFacetInTopFilters(facet.attribute) ||
          (facet.attribute === 'normalisedTitle' && (facet.isActive || facet.isExcluded || facet.isOnly)),
      ),
    [allFacetsWithActive],
  );

  // Use allowedFacets as-is (unfiltered by query) so the pill list stays stable.
  const filteredFacets = allowedFacets;

  // Separate active and inactive facets
  const activeFacets = useMemo(
    () => filteredFacets.filter((f) => f.isActive || f.isExcluded || f.isOnly),
    [filteredFacets],
  );
  const inactiveFacets = useMemo(
    () => filteredFacets.filter((f) => !f.isActive && !f.isExcluded && !f.isOnly),
    [filteredFacets],
  );

  // Group active facets by attribute and build final sorted array
  const allFacets = useMemo(() => {
    try {
      // Group active facets by attribute
      const activeFacetsByAttribute = new Map<string, typeof activeFacets>();
      activeFacets.forEach((facet) => {
        if (!activeFacetsByAttribute.has(facet.attribute)) {
          activeFacetsByAttribute.set(facet.attribute, []);
        }
        activeFacetsByAttribute.get(facet.attribute)!.push(facet);
      });

      // Sort active facets within each attribute group
      activeFacetsByAttribute.forEach((facets) => {
        if (facets[0]?.attribute === locationAttribute) {
          facets.sort((a, b) => {
            const aGroup = a.locationGroup || '';
            const bGroup = b.locationGroup || '';
            if (aGroup !== bGroup) {
              if (aGroup === 'finland') return -1;
              if (bGroup === 'finland') return 1;
              return aGroup.localeCompare(bGroup);
            }
            return a.label.localeCompare(b.label);
          });
        } else {
          facets.sort((a, b) => a.label.localeCompare(b.label));
        }
      });

      // Build final sorted array: grouped active facets first, then inactive facets
      const result: FacetItem[] = [];
      const attributeOrder = TOP_FILTER_ATTRIBUTE_ORDER;

      // Add active facets grouped by attribute
      attributeOrder.forEach((attr) => {
        const facets = activeFacetsByAttribute.get(attr);
        if (facets) {
          result.push(...facets);
        }
      });

      // Add any remaining active facets: every value of each attribute, kept together so they
      // group into one bracket (this used to add only the FIRST value of each such attribute).
      const addedAttributes = new Set<string>(
        attributeOrder.filter((attr) => activeFacetsByAttribute.has(attr)) as string[],
      );
      activeFacetsByAttribute.forEach((facets, attr) => {
        if (!addedAttributes.has(attr)) {
          result.push(...facets);
          addedAttributes.add(attr);
        }
      });

      // Curated default suggestions (legal review: the old raw top-count-across-every-attribute
      // sort surfaced confusing defaults like "No People Management"). Remote/Hybrid first, then
      // the highest-count locations, then whatever's left sorted by count as before, with
      // peopleManagementTier never suggested (still shows normally if a visitor sets it via
      // Advanced Filters; this only curates what's *offered*, not what can be active).
      const eligibleInactive = inactiveFacets.filter((f) => f.attribute !== 'peopleManagementTier');
      const remotenessPriority = eligibleInactive
        .filter(
          (f) =>
            f.attribute === 'remoteness' &&
            (REMOTE_VALUES.has(f.value.toLowerCase()) || HYBRID_VALUES.has(f.value.toLowerCase())),
        )
        .sort((a, b) =>
          REMOTE_VALUES.has(a.value.toLowerCase()) ? -1 : REMOTE_VALUES.has(b.value.toLowerCase()) ? 1 : 0,
        );
      const topLocations = eligibleInactive
        .filter((f) => f.attribute === locationAttribute)
        .sort((a, b) => b.count - a.count);
      const prioritized = [...remotenessPriority, ...topLocations];
      const prioritizedKeys = new Set(prioritized.map((f) => `${f.attribute}:${f.value}`));
      const remainingSorted = eligibleInactive
        .filter((f) => !prioritizedKeys.has(`${f.attribute}:${f.value}`))
        .sort((a, b) => b.count - a.count);
      const sortedInactiveFacets = [...prioritized, ...remainingSorted];
      const MAX_FACETS = 20;
      const activeCount = result.length;
      const remainingSlots = Math.max(0, MAX_FACETS - activeCount);
      const limitedInactiveFacets = sortedInactiveFacets.slice(0, remainingSlots);

      result.push(...limitedInactiveFacets);
      return result;
    } catch (error) {
      return [];
    }
  }, [activeFacets, inactiveFacets]);

  // Active/inactive split and active-facets-grouped-by-attribute, hoisted out of renderItems'
  // useMemo (TEC navbar redesign Phase 2 item 2.1) so the new tape renderer can reuse exactly
  // the same groups the classic bracket-and-OR pill row is built from, rather than a second,
  // possibly-drifting copy of this grouping logic. Behaviourally identical to before: renderItems
  // below just reads these instead of recomputing them inline.
  const activeFacetsList = useMemo(() => allFacets.filter((f) => f.isActive || f.isExcluded || f.isOnly), [allFacets]);
  const inactiveFacetsList = useMemo(
    () => allFacets.filter((f) => !f.isActive && !f.isExcluded && !f.isOnly),
    [allFacets],
  );

  const activeGroups = useMemo(() => {
    const groups: Array<{ attribute: string; facets: typeof activeFacetsList; locationGroup?: 'finland' | 'remote' }> =
      [];
    let currentGroup: typeof activeFacetsList = [];
    let currentAttribute: string | null = null;
    let currentLocationGroup: 'finland' | 'remote' | undefined = undefined;

    activeFacetsList.forEach((facet) => {
      const facetLocationGroup = facet.locationGroup;
      const groupKey =
        facet.attribute === locationAttribute ? `${facet.attribute}_${facetLocationGroup || 'other'}` : facet.attribute;
      const prevGroupKey =
        currentAttribute === locationAttribute
          ? `${currentAttribute}_${currentLocationGroup || 'other'}`
          : currentAttribute;

      if (currentAttribute === null || groupKey !== prevGroupKey) {
        if (currentGroup.length > 0 && currentAttribute !== null) {
          groups.push({
            attribute: currentAttribute,
            facets: [...currentGroup],
            locationGroup: currentLocationGroup,
          });
        }
        currentGroup = [facet];
        currentAttribute = facet.attribute;
        currentLocationGroup = facetLocationGroup;
      } else {
        currentGroup.push(facet);
      }
    });

    if (currentGroup.length > 0 && currentAttribute !== null) {
      groups.push({
        attribute: currentAttribute,
        facets: [...currentGroup],
        locationGroup: currentLocationGroup,
      });
    }
    return groups;
  }, [activeFacetsList]);

  const hasClearableFilters = useMemo(
    () => Object.values(filtersObj).some((arr) => Array.isArray(arr) && arr.length > 0),
    [filtersObj],
  );

  const handleClearAllFilters = useCallback(() => {
    pushIndexUiState({
      query: query ?? '',
      refinementList: {},
      page: 0,
      sort,
    });
  }, [pushIndexUiState, query, sort]);

  const handleFacetClick = useCallback(
    (attribute: string, value: string, mode: TopFilterMode = 'include') => {
      try {
        const excludeKey = `exclude_${attribute}`;
        const onlyKey = `only_${attribute}`;
        const currentValues = filtersObj[attribute] || [];
        const excludedValues = filtersObj[excludeKey] || [];
        const onlyValues = filtersObj[onlyKey] || [];
        const isActive = currentValues.some((v) => v.toLowerCase() === value.toLowerCase());

        const newFilters = { ...filtersObj };

        if (mode === 'exclude') {
          // Clicking an already-excluded pill clears the exclusion; it must not also
          // fall through to the include branch below or the filter becomes
          // contradictory (attribute:=X && attribute:!=X), silently zeroing results.
          const remaining = excludedValues.filter((v) => v.toLowerCase() !== value.toLowerCase());
          if (remaining.length > 0) {
            newFilters[excludeKey] = remaining;
          } else {
            delete newFilters[excludeKey];
          }
        } else if (mode === 'only') {
          // Clicking an already-"only" pill clears it, same as clearing an exclusion above.
          const remaining = onlyValues.filter((v) => v.toLowerCase() !== value.toLowerCase());
          if (remaining.length > 0) {
            newFilters[onlyKey] = remaining;
          } else {
            delete newFilters[onlyKey];
          }
        } else if (isActive) {
          const remaining = currentValues.filter((v) => v.toLowerCase() !== value.toLowerCase());
          if (remaining.length > 0) {
            newFilters[attribute] = remaining;
          } else {
            delete newFilters[attribute];
          }
        } else {
          const existingIndex = currentValues.findIndex((v) => v.toLowerCase() === value.toLowerCase());
          if (existingIndex !== -1) {
            const updated = [...currentValues];
            updated[existingIndex] = value;
            newFilters[attribute] = updated;
          } else {
            newFilters[attribute] = [...currentValues, value];
          }
        }

        const hasAny = Object.keys(newFilters).length > 0;
        pushIndexUiState({
          query: query ?? '',
          refinementList: hasAny ? newFilters : {},
          page: 0,
          sort,
        });
      } catch (error) {
        // Error handling
      }
    },
    [filtersObj, pushIndexUiState, query, sort],
  );

  // Posted date (firstSeenMin) is a real filter (TEC navbar redesign Phase 2 item 2.3: it counts
  // toward the Advanced Filters badge, see applied-filter-count.ts) but is deliberately excluded
  // from allFacets/activeGroups above (EXCLUDED_FROM_ADDITIONAL_FACETS: it never had a
  // facetsData entry, only a stored threshold), so the tape renders it as one more token built
  // directly from filtersObj, reusing the exact same timestamp->label matching AllFiltersModal's
  // new Posted section (LastSeenSelector.tsx) uses, rather than a second copy of that mapping.
  const lastSeenValue = filtersObj.firstSeenMin?.[0];
  const lastSeenLabel = lastSeenValue ? resolveLastSeenLabel(lastSeenValue, formatMessage) : null;
  const handleClearLastSeen = useCallback(() => {
    const newFilters = { ...filtersObj };
    delete newFilters.firstSeenMin;
    const hasAny = Object.keys(newFilters).length > 0;
    pushIndexUiState({ query: query ?? '', refinementList: hasAny ? newFilters : {}, page: 0, sort });
  }, [filtersObj, pushIndexUiState, query, sort]);

  if (activeGroups.length === 0 && !lastSeenLabel) return null;
  // A quick-add that matches every current result narrows nothing: on a town page the town's
  // own place hierarchy (Viitasaari, Suomi, Keski-Suomi, Eurooppa) all carry the full count, and
  // offering them put a row of no-op chips above the H1.
  const totalHits = results?.nbHits ?? 0;
  const quickAdds = inactiveFacetsList.filter((f) => !(totalHits > 0 && f.count >= totalHits)).slice(0, 4);
  const sortNote =
    sort === 'date'
      ? formatMessage(tapeMessages.sortNewest)
      : sort === 'salary'
        ? formatMessage(tapeMessages.sortSalary)
        : null;

  // One token per attribute group, plus the posted-date token if set, reused verbatim by both
  // the desktop tape (grouped, values joined by "or" inside one token) and the mobile tape
  // (flattened to one removable token per value, since 2.5 requires each value to be its own
  // 44px tap target with its own remove mark, not a shared multi-value token).
  type TapeTokenGroup =
    | { key: string; facets: FacetItem[]; isLastSeen?: false }
    | { key: 'firstSeenMin'; label: string; isLastSeen: true };
  // A role page (/en/jobs/role/carpenter) selects the role's whole title-value set, which in en/sv
  // includes the untranslated Finnish titles the same jobs carry ("Carpenter or Kirvesmies or
  // Timpuri"). When the included titles are exactly one role's set, the tape names the role once,
  // by its display name, and removing it clears the set (same rule as roleHubTitle in
  // build-active-filters.ts, which names the page's H1).
  const roleLocale = normalizeSupportedLocale(intl.locale);
  const includedTitles = filtersObj.normalisedTitle ?? [];
  const titleRole = includedTitles.length
    ? findRoleForNormalisedTitle(String(includedTitles[0]), roleLocale)
    : null;
  const roleTitleLabel =
    titleRole && titleValuesMatchRole(titleRole, roleLocale, includedTitles.map(String))
      ? titleRole.displayNames[roleLocale]
      : null;
  const handleRemoveRoleTitles = () => {
    const newFilters = { ...filtersObj };
    delete newFilters.normalisedTitle;
    pushIndexUiState({
      query: query ?? '',
      refinementList: Object.keys(newFilters).length > 0 ? newFilters : {},
      page: 0,
      sort,
    });
  };
  const collapseRoleTitles = (facets: FacetItem[]): FacetItem[] => {
    if (!roleTitleLabel || facets[0]?.attribute !== 'normalisedTitle') return facets;
    const included = facets.filter((f) => f.isActive && !f.isExcluded && !f.isOnly);
    if (!included.length) return facets;
    return [
      { ...included[0], value: ROLE_TITLES_VALUE, label: roleTitleLabel },
      ...facets.filter((f) => !included.includes(f)),
    ];
  };
  const removeFacet = (facet: FacetItem) =>
    facet.value === ROLE_TITLES_VALUE
      ? handleRemoveRoleTitles()
      : handleFacetClick(facet.attribute, facet.value, facetMode(facet.isExcluded, facet.isOnly));

  const tokenGroups: TapeTokenGroup[] = [
    ...activeGroups.map((group, idx) => ({
      key: `group-${group.attribute}-${idx}`,
      facets: collapseRoleTitles(group.facets),
    })),
    ...(lastSeenLabel ? [{ key: 'firstSeenMin' as const, label: lastSeenLabel, isLastSeen: true as const }] : []),
  ];

  const desktopValue = (facet: FacetItem, isLastInGroup: boolean) => (
    <React.Fragment key={`${facet.attribute}-${facet.value}`}>
      {facet.isExcluded ? <span className="text-jc-ink">{formatMessage(tapeMessages.not)} </span> : null}
      {facet.isOnly ? <span className="text-jc-forest">{formatMessage(tapeMessages.only)} </span> : null}
      <span
        role="button"
        tabIndex={0}
        onClick={() => removeFacet(facet)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            removeFacet(facet);
          }
        }}
        className="cursor-pointer hover:text-jc-ink hover:line-through [text-decoration-thickness:1.5px]"
      >
        {facet.attribute === 'companyId' ? <CompanyName name={facet.label} /> : facet.label}
      </span>
      {/* mx-1 carries the visual gap: inside the inline-flex token a flex item's leading/trailing
          spaces collapse (it rendered "OnsiteorRemote"). The spaces stay for text content. */}
      {!isLastInGroup ? (
        <span className="font-normal text-jc-ink-secondary mx-1"> {formatMessage(tapeMessages.or)} </span>
      ) : null}
    </React.Fragment>
  );

  return (
    <>
      {/* Desktop tape (2.1/2.4): one 34px row, collapses on scroll same as the index strip. */}
      <div
        className="hidden lg:flex items-center border-t border-jc-border bg-jc-surface overflow-hidden px-[22px] transition-[height,opacity] duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none"
        style={tapeCollapsed ? { height: 0, opacity: 0, borderTopColor: 'transparent' } : { height: 34, opacity: 1 }}
        aria-hidden={tapeCollapsed}
        aria-label={formatMessage({
          id: 'filters.top.ariaLabel',
          defaultMessage: 'Filter categories',
          description: 'Accessibility label for the filter tape/pill row',
        })}
      >
        <div className="flex items-center min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden flex-1 gap-1.5">
          {tokenGroups.map((token, idx) => (
            <React.Fragment key={token.key}>
              <span className="inline-flex items-center h-6 px-[9px] rounded-jc-info bg-jc-elevated text-jc-ink text-xs font-semibold whitespace-nowrap flex-shrink-0">
                {token.isLastSeen ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClearLastSeen}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClearLastSeen();
                      }
                    }}
                    className="cursor-pointer hover:text-jc-ink hover:line-through [text-decoration-thickness:1.5px]"
                  >
                    {token.label}
                  </span>
                ) : (
                  token.facets.map((facet, fi) => desktopValue(facet, fi === token.facets.length - 1))
                )}
              </span>
              {idx < tokenGroups.length - 1 ? (
                <span className="text-xs text-jc-ink-secondary flex-shrink-0">
                  {' '}
                  {formatMessage(tapeMessages.and)}{' '}
                </span>
              ) : null}
            </React.Fragment>
          ))}
          {quickAdds.length > 0 ? (
            <span className="w-px h-3.5 bg-jc-border flex-shrink-0 mx-0.5" aria-hidden />
          ) : null}
          {quickAdds.map((facet) => (
            <button
              key={`add-${facet.attribute}-${facet.value}`}
              type="button"
              onClick={() => handleFacetClick(facet.attribute, facet.value, 'include')}
              className="inline-flex items-center h-6 px-[9px] rounded-jc-info border border-dashed border-jc-border text-jc-ink-secondary text-xs whitespace-nowrap flex-shrink-0 cursor-pointer hover:text-jc-ink-secondary hover:border-jc-ink-secondary"
            >
              {formatMessage(tapeMessages.quickAddPrefix, { label: facet.label })}
            </button>
          ))}
        </div>
        <span className="ml-auto inline-flex items-center gap-3 pl-3 flex-shrink-0">
          {sortNote ? <span className="text-xs text-jc-ink-secondary whitespace-nowrap">{sortNote}</span> : null}
          {hasClearableFilters ? (
            <Button variant="tertiary" size="sm" onClick={handleClearAllFilters} className="whitespace-nowrap px-3">
              {formatMessage(tapeMessages.clearAll)}
            </Button>
          ) : null}
        </span>
      </div>

      {/* Mobile tape (2.5): each value is its own 44px tappable, always-removable token; no
          quick-adds (unfiltered mobile carries the top-bar subtitle from Phase 1 instead).
          Review fix P3: the classic mobile row (below) carried the icon logo as the only home
          link on the mobile SRP; this tape lacked it entirely. Same Logo `icon` variant,
          44px tap box, same clearSearch behaviour. */}
      <div className="flex lg:hidden items-center h-14 bg-jc-surface overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-3 gap-1">
        {clearSearch != null && (
          <Logo
            clearSearch={clearSearch}
            variant="icon"
            locale={intl.locale}
            className="w-11 h-11 -ml-1 mr-0.5 flex-shrink-0"
          />
        )}
        {tokenGroups.map((token, groupIdx) => {
          const values = token.isLastSeen
            ? [
                {
                  key: 'firstSeenMin',
                  label: token.label,
                  spoken: token.label,
                  excluded: false,
                  onRemove: handleClearLastSeen,
                },
              ]
            : token.facets.map((facet) => {
                const prefix = facet.isOnly ? `${formatMessage(tapeMessages.only)} ` : '';
                const spoken = `${facet.isExcluded ? `${formatMessage(tapeMessages.not)} ` : prefix}${facet.label}`;
                return {
                  key: `${facet.attribute}-${facet.value}`,
                  label: `${prefix}${facet.label}`,
                  spoken,
                  excluded: facet.isExcluded,
                  onRemove: () => removeFacet(facet),
                };
              });
          return (
            <React.Fragment key={token.key}>
              {values.map((v, vi) => (
                <React.Fragment key={v.key}>
                  <FilterPill
                    label={v.label}
                    excluded={v.excluded}
                    onRemove={v.onRemove}
                    removeLabel={formatMessage(tapeMessages.removeFilter, { label: v.spoken })}
                    className="flex-shrink-0"
                  />
                  {vi < values.length - 1 ? (
                    <span className="text-sm text-jc-ink-secondary flex-shrink-0 mx-1">
                      {' '}
                      {formatMessage(tapeMessages.or)}{' '}
                    </span>
                  ) : null}
                </React.Fragment>
              ))}
              {groupIdx < tokenGroups.length - 1 ? (
                <span className="text-sm text-jc-ink-secondary flex-shrink-0">
                  {' '}
                  {formatMessage(tapeMessages.and)}{' '}
                </span>
              ) : null}
            </React.Fragment>
          );
        })}
        {hasClearableFilters ? (
          <Button
            variant="tertiary"
            size="sm"
            onClick={handleClearAllFilters}
            className="whitespace-nowrap flex-shrink-0 px-3 mr-1"
          >
            {formatMessage(tapeMessages.clearAll)}
          </Button>
        ) : null}
      </div>
    </>
  );
};
